import { toast } from "vue3-toastify";
import { axios_instance } from "./api";
import { file_to_module_b64, type PaginatedResponse } from "./action";

export interface FindDMLScanAction {
  id: string;
  status: string;
  type: number;
  created: number;
  url: string;
  runtime: number;
}

export interface FindDMLEvent {
  id: string;
  agent_id: string;
  campaign_id?: string;
  scan_id: string;
  time_of_day: string;
  process_name: string;
  pid: string;
  operation: string;
  path: string;
  result: string;
  detail?: string;
  created: number;
  modified: number;
}

export interface FindDMLHijackAction {
  id: string;
  status: string;
  type: number;
  created: number;
  parent_event_id: string;
  save_dir: string;
  dll_name_on_victim: string;
  clsid?: string;
  rollback_type?: string;
  rollback_path?: string;
  rollback_clsid?: string;
  rollback_status?: string;
  exploit_result_code?: number;
  log?: string;
  url: string;
}

export type FindDMLModuleType =
  | "find-dll-missing-scan"
  | "find-dml-hijack"
  | "find-dml-com-hijack"
  | "find-dml-dll-hijack-rollback"
  | "find-dml-com-hijack-rollback";

export interface FindDMLModule {
  id: string;
  file_name: string;
  type: FindDMLModuleType;
  created: number;
}

export const listFindDMLModules = async (type: FindDMLModuleType): Promise<FindDMLModule[]> => {
  const response = await axios_instance.post<{ data: Record<string, unknown>[] }>(
    "/api/v1/view/version/pagination",
    { type: [type], page: 1, size: 100 },
  );
  return (response.data.data ?? []).map((row) => ({
    id: String(row.id),
    file_name: String(row.file_name ?? row.version),
    type: row.type as FindDMLModuleType,
    created: Number(row.created ?? 0),
  }));
};

export const uploadFindDMLModule = async ({
  agentId,
  type,
  file,
}: {
  agentId: string;
  type: FindDMLModuleType;
  file: File;
}): Promise<FindDMLModule> => {
  const response = await axios_instance.post<{ data: FindDMLModule }>(
    "/api/v1/view/find-dml/modules/upload",
    { agent_id: agentId, type, file_name: file.name, module_b64: await file_to_module_b64(file) },
  );
  return response.data.data;
};

// ponytail: start a find-dll-missing scan. module_b64 is the procmon tool the
// agent pulls via GET /agent/module/:id, runs to collect .pml, then uploads back.
export const createFindDMLScan = async ({
  agentId,
  runtime,
  moduleFile,
  moduleVersionId,
}: {
  agentId: string;
  runtime: number;
  moduleFile?: File;
  moduleVersionId?: string;
}): Promise<string> => {
  let module_b64: string | undefined;
  if (moduleFile) module_b64 = await file_to_module_b64(moduleFile);

  const response = await axios_instance.post<{
    message: string;
    action_id: string;
    status: string;
  }>("/api/v1/view/find-dml/scan", {
    agent_id: agentId,
    runtime,
    ...(module_b64 ? { module_b64 } : {}),
    ...(moduleVersionId ? { module_version_id: moduleVersionId } : {}),
  });

  if (response.data.message !== "ok") {
    toast.error(response.data.message || "Không thể bắt đầu quét DLL missing.");
  }
  return response.data.action_id;
};

export const listFindDMLActions = async (
  agentId: string,
  page: number,
  size: number,
): Promise<PaginatedResponse<FindDMLScanAction>> => {
  const response = await axios_instance.post<PaginatedResponse<FindDMLScanAction>>(
    "/api/v1/view/find-dml/actions",
    { agent_id: agentId, page, size },
  );
  return response.data;
};

export const listFindDMLEvents = async (
  agentId: string,
  scanId: string | undefined,
  page: number,
  size: number,
  search = "",
): Promise<PaginatedResponse<FindDMLEvent>> => {
  const response = await axios_instance.post<
    PaginatedResponse<FindDMLEvent>
  >("/api/v1/view/find-dml/events", { agent_id: agentId, scan_id: scanId, page, size, search });
  return response.data;
};

export const createFindDMLHijack = async ({
  agentId,
  type,
  parentEventId,
  saveDir,
  dllNameOnVictim,
  clsid,
  moduleFile,
  moduleVersionId,
}: {
  agentId: string;
  type?: "dll-hijack" | "com-hijack";
  parentEventId?: string;
  saveDir?: string;
  dllNameOnVictim?: string;
  clsid?: string;
  moduleFile?: File;
  moduleVersionId?: string;
}): Promise<string> => {
  let module_b64: string | undefined;
  if (moduleFile) module_b64 = await file_to_module_b64(moduleFile);

  const response = await axios_instance.post<{
    message: string;
    action_id: string;
    status: string;
  }>("/api/v1/view/find-dml/hijack", {
    agent_id: agentId,
    type: type ?? "dll-hijack",
    ...(parentEventId ? { parent_event_id: parentEventId } : {}),
    save_dir: saveDir,
    dll_name_on_victim: dllNameOnVictim,
    ...(type === "com-hijack" && clsid ? { clsid } : {}),
    ...(module_b64 ? { module_b64 } : {}),
    ...(moduleVersionId ? { module_version_id: moduleVersionId } : {}),
  });

  if (response.data.message !== "ok") {
    toast.error(response.data.message || "Không thể bắt đầu khai thác.");
  }
  return response.data.action_id;
};

export const listFindDMLHijacks = async (
  agentId: string,
  type: string | undefined,
  status: string | undefined,
  page: number,
  size: number,
): Promise<PaginatedResponse<FindDMLHijackAction>> => {
  const response = await axios_instance.post<
    PaginatedResponse<FindDMLHijackAction>
  >("/api/v1/view/find-dml/hijacks", { agent_id: agentId, type, status, page, size });
  return response.data;
};

export const triggerFindDmlRollback = async ({
  agentId,
  hijackActionId,
  moduleFile,
  moduleVersionId,
}: {
  agentId?: string;
  hijackActionId: string;
  moduleFile?: File;
  moduleVersionId?: string;
}): Promise<string> => {
  const module_b64 = moduleFile ? await file_to_module_b64(moduleFile) : undefined;
  const response = await axios_instance.post<{
    message: string;
    action_id: string;
  }>("/api/v1/view/find-dml/rollback", {
    agent_id: agentId,
    hijack_action_id: hijackActionId,
    ...(module_b64 ? { module_b64 } : {}),
    ...(moduleVersionId ? { module_version_id: moduleVersionId } : {}),
  });
  return response.data.action_id;
};
